import { createSlice } from '@reduxjs/toolkit';

// The upload state of one image; other features (the OCR read) wait on it, so they import these, never the strings.
export const IMAGE_UPLOAD_STATUS = Object.freeze({
    UPLOADING: 'uploading',
    IN_FLIGHT: 'inFlight',
    SAVED: 'saved',
    FAILED: 'failed',
});

// The base64 of an agent snapshot is read only to build the upload file, so it is dropped once nothing can upload the
// image again: after the upload is saved, or after its transaction closed and the upload already started. Kept, every
// snapshot of a long desk session stays in the renderer until it runs out of memory (BEAST-14082). Batches stay in
// place: they are addressed by index.
// Every close removes the transaction through setTransactions, also the flows that call the close saga directly. Matched
// by type: transactionsSlice imports this slice through utils.
const SET_TRANSACTIONS = 'transactions/setTransactions';

const canReleaseEncodedImage = (batch, img) =>
    img.status === IMAGE_UPLOAD_STATUS.SAVED || (batch.isTransactionClosed && img.status !== IMAGE_UPLOAD_STATUS.UPLOADING);

const releaseEncodedImage = (state, batch, img) => {
    if (!img.fileProps?.encodedImage || !canReleaseEncodedImage(batch, img)) return;
    img.fileProps.encodedImage = null;
    // not saved and no longer uploadable: if the transaction shows the snapshot again, it is a new upload
    if (img.status !== IMAGE_UPLOAD_STATUS.SAVED) delete state.queuedImageKeys?.[batch.transactionId]?.[img.id];
};

const initialState = {
    batches: [],
    transactionStatuses: {}, // { [transactionId]: 'not_submitted' | 'submitted' | 'discarded' }
    queuedImageKeys: {}, // { [transactionId]: { [imageKey]: true } } - snapshots already sent to presign
    // Macaw images uploaded by the backend, for the upload indicator; kept after submit / discard (the form is gone)
    macawUploads: {}, // { [transactionId]: { transactionId, locationId, locationName, name, origin, images: { [id]: status } } }
    deletingAssetIds: {}, // { [assetId]: true } - delete requested, waiting for the backend
};

const storeTransactionImageSlice = createSlice({
    name: 'storeTransactionImage',
    initialState,
    reducers: {
        // trigger for the upload saga: new agent snapshots of a transaction, deduplicated there
        uploadRequested() {},

        markImagesQueued(state, { payload: { transactionId, keys } }) {
            if (!state.queuedImageKeys) state.queuedImageKeys = {};
            if (!state.queuedImageKeys[transactionId]) state.queuedImageKeys[transactionId] = {};
            keys.forEach((key) => {
                state.queuedImageKeys[transactionId][key] = true;
            });
        },

        // presign failed: the next uploadRequested tries these snapshots again
        unmarkImagesQueued(state, { payload: { transactionId, keys } }) {
            const queued = state.queuedImageKeys?.[transactionId];
            if (!queued) return;
            keys.forEach((key) => {
                delete queued[key];
            });
        },

        macawImagesStatusChanged(state, { payload: { transactionId, locationId, locationName, name, images } }) {
            if (!transactionId || !images?.length) return;
            if (!state.macawUploads) state.macawUploads = {};
            const entry = state.macawUploads[transactionId] || { transactionId, origin: 'ALISA', images: {} };
            // the transaction may be gone from the desk (submitted / discarded): keep what is known
            entry.locationId = locationId ?? entry.locationId;
            entry.locationName = locationName ?? entry.locationName;
            entry.name = entry.name ?? name;
            images.forEach(({ id, status }) => {
                if (entry.images[id] === 'READY') return;
                entry.images[id] = status;
            });
            state.macawUploads[transactionId] = entry;
        },

        appendBatch(state, { payload }) {
            const { transactionId } = payload;
            if (transactionId && !state.transactionStatuses[transactionId]) {
                state.transactionStatuses[transactionId] = 'not_submitted';
            }
            state.batches.push({
                ...payload,
                images: payload.images.map((img) => ({
                    ...img,
                    status: img.status || IMAGE_UPLOAD_STATUS.UPLOADING,
                    presignPutUrl: img.presignPutUrl || null,
                    error: img.error || null,
                    assetId: img.assetId || null,
                    isFinalFailed: false,
                })),
            });
        },
        setPresignUrl(state, { payload }) {
            const { batchIndex, imageIndex, presignPutUrl, assetId } = payload;
            const batch = state.batches[batchIndex];
            if (batch) {
                const img = batch.images[imageIndex];
                if (img) {
                    img.presignPutUrl = presignPutUrl;
                    img.assetId = assetId;
                }
            }
        },
        setImageStatus(state, { payload }) {
            const { batchIndex, imageIndex, status, error, isFinalFailed } = payload;
            const batch = state.batches[batchIndex];
            if (batch) {
                const img = batch.images[imageIndex];
                if (img) {
                    img.status = status;
                    img.error = error || null;
                    img.isFinalFailed = isFinalFailed;
                    releaseEncodedImage(state, batch, img);
                }
            }
        },
        retryImageUpload(state, { payload }) {
            const { batchIndex, imageIndex } = payload;
            const batch = state.batches[batchIndex];
            if (batch) {
                const img = batch.images[imageIndex];
                if (img) {
                    img.status = IMAGE_UPLOAD_STATUS.UPLOADING;
                    img.error = null;
                    img.isFinalFailed = false;
                }
            }
        },
        confirmTransaction(state, { payload }) {
            const { transactionId } = payload;
            if (transactionId) {
                state.transactionStatuses[transactionId] = 'submitted';
            }
        },
        discardTransaction(state, { payload }) {
            const { transactionId } = payload;
            if (transactionId) {
                state.transactionStatuses[transactionId] = 'discarded';
            }
        },
        deleteImage(state, { payload: { assetId } }) {
            if (!assetId) return;
            if (!state.deletingAssetIds) state.deletingAssetIds = {};
            state.deletingAssetIds[assetId] = true;
        },
        // success or failure: the delete button leaves its loading state
        deleteImageFinished(state, { payload: { assetId } }) {
            if (state.deletingAssetIds) delete state.deletingAssetIds[assetId];
        },
        deleteImageSuccess(state, { payload }) {
            const { assetId, transactionId, imageId } = payload;
            state.batches = state.batches.map((batch) => ({
                ...batch,
                images: batch.images.filter((img) => img.assetId !== assetId),
            }));
            // a deleted Macaw image is not uploaded again: the upload indicator stops following it
            const macawUpload = state.macawUploads?.[transactionId];
            if (macawUpload && imageId in macawUpload.images) {
                delete macawUpload.images[imageId];
                if (Object.keys(macawUpload.images).length === 0) delete state.macawUploads[transactionId];
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(SET_TRANSACTIONS, (state, { payload: transactions = [] }) => {
            const openTransactionIds = new Set(transactions.map(({ id }) => String(id)));
            state.batches.forEach((batch) => {
                if (openTransactionIds.has(String(batch.transactionId))) return;
                batch.isTransactionClosed = true;
                batch.images.forEach((img) => releaseEncodedImage(state, batch, img));
            });
        });
    },
});

export const transactionImageActions = storeTransactionImageSlice.actions;
export const transactionImageReducer = storeTransactionImageSlice.reducer;
